import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { RoutePharmacistModal } from '../../components/common/routePharmacistModal';
import { RouteControlSheet } from '../../components/common/routeControlSheet';
import { AssistantPharmacistSwitchModal } from '../../components/common/assistantPharmacistSwitchModal';
import { TAB } from '../../components/common/routeControlSheet/constants';
import SearchBox from '../../components/common/searchBox';
import { useAllSelector } from '../../services/selectors/allSelector';
import { useAuthSelector } from '../../services/selectors/authSelector';
import { useSuperVisorSelector } from '../../services/selectors/allSuperVisorSelector';
import { fetchPharmacistOfPharmacy } from '../../services/apis/dispense/pharmacistOfPharmacy';
import {
	fetchAllPharmacistList,
	fetchCheckUnassignedVitalPatients,
	savePharmacistForDriver,
} from '../../services/slices/supervisor/pendingCounselling';
import { authSelectedPharmacist } from '../../services/apis/auth/selectedPharmacistID';
import { clearAllTodayDispenseData } from '../../services/slices/dispense/patientForRouteList';
import { getActivityStatus } from '../../services/slices/chat/activityStatus';
import { getDefaultPharmacist } from '../../utils/helper/helper';

import * as Icons from '../../assets/svg/icons';
import * as Common from '../../components/common';
import * as Hooks from '../../hooks';
import * as routeNames from '../../routes/routeName';

const ICON_BOX_STYLE = { width: '40px', height: '40px' };
const LABEL_STYLE = { fontSize: '13px', fontWeight: '500' };

const DashboardCard = ({ icon, label, cardClassName, iconBoxStyle = ICON_BOX_STYLE, cardStyle, onClick, badge }) => (
	<div className={`card relative flex flex-col p-3 rounded-md gap-3 ${cardClassName}`} style={cardStyle} onClick={onClick}>
		{badge > 0 && (
			<span className="absolute -right-1.5 -top-1.5 z-10 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white shadow-md">
				{badge > 9 ? '9+' : badge}
			</span>
		)}
		<div className="rounded-md flex items-center justify-center" style={iconBoxStyle}>{icon}</div>
		<div className="text-info">
			<h1 className="text-sm font-medium" style={LABEL_STYLE}>{label}</h1>
		</div>
	</div>
);

const DriverDashboard = () => {
	const navigate = Hooks.useCustomNavigate();
	const dispatch = Hooks.useCustomDispatch();
	const location = useLocation();

	const [isDelivered, setIsDelivered] = useState(false);
	const [showModel, setShowModel] = useState(false);
	const [showRouteControl, setShowRouteControl] = useState(false);
	const [routeControlRequestedTab, setRouteControlRequestedTab] = useState(null);
	const [showAssistantPharmacistSwitch, setShowAssistantPharmacistSwitch] = useState(false);
	const [assistantSwitchError, setAssistantSwitchError] = useState('');
	const [assistantSwitchSaving, setAssistantSwitchSaving] = useState(false);
	const [apiCallMade, setApiCallMade] = useState(false);
	const [pendingHandoverCount, setPendingHandoverCount] = useState(0);
	const hasHandledAutoOpenRoutePickerRef = useRef(false);
	const hasHandledAutoOpenRouteControlRef = useRef(false);

	const {
		finalMissedCount,
		pharmacistOfPharmacyListResponse,
		supervisor_pharmacist,
		supervisorOfTheDay,
		pharmacy_assign_chats,
		hasUnassignedVital,
	} = useAllSelector();

	const { allPharmacistListResponse } = useSuperVisorSelector();

	const {
		pharmacyId,
		isSupervisor,
		authPharmacy,
		driverDefaultPharmacist,
		flexiUser,
		hcpUser,
		isPharmacistForSelectedPharmacy,
		selectedPharmacistID,
	} = useAuthSelector();

	const showSearchBox = useMemo(
		() => !(hcpUser === 0 && flexiUser) && !(hcpUser === 0) && !flexiUser,
		[hcpUser, flexiUser]
	);

	const showSupervisorCard = (isSupervisor === 1 && supervisor_pharmacist) || supervisorOfTheDay;
	const showMissedDispense = pharmacistOfPharmacyListResponse?.data !== null && finalMissedCount !== 0;
	const showAddVitalsCard = hcpUser === 1 || hasUnassignedVital;
	const showSwitchPharmacy = authPharmacy?.length !== 1;
	const defaultPharmacist = useMemo(
		() => getDefaultPharmacist(pharmacyId, driverDefaultPharmacist),
		[pharmacyId, driverDefaultPharmacist]
	);
	const pharmacistUser = defaultPharmacist?.[0]?.driver_pharmacist_user;
	const showPharmacistSelection = pharmacistUser?.type !== 'pharmacist';
	const isSupervisorUser = [1, '1', true].includes(isSupervisor);
	const canSwitchAssistantPharmacist = showPharmacistSelection && !isSupervisorUser && !isPharmacistForSelectedPharmacy;
	const isLoading = pharmacistOfPharmacyListResponse?.loading;
	const shouldAutoOpenRoutePicker = new URLSearchParams(location.search).get('openRoutePicker') === '1';
	const shouldAutoOpenRouteControl = new URLSearchParams(location.search).get('openRouteControl') === '1';
	// Optional deep-link tab, e.g. ?openRouteControl=1&tab=requests opens the Incoming section
	// (used by the handover-request notification tap). Defaults to Handover.
	const routeControlTabParam = new URLSearchParams(location.search).get('tab');
	const currentAssistantPharmacistId = selectedPharmacistID || '';
	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		if (pharmacyId && !apiCallMade && location?.pathname === routeNames.DASHBOARD_PHARMACY) {
			dispatch(fetchPharmacistOfPharmacy(pharmacyId));
			dispatch(fetchCheckUnassignedVitalPatients({ pharmacyId }));
			dispatch(clearAllTodayDispenseData());
			setApiCallMade(true);
		}
	}, [apiCallMade, pharmacyId, location.pathname, dispatch]);

	const openTodayDispenseModal = useCallback(() => {
		setIsDelivered(false);
		setShowModel(true);
	}, []);

	const openRouteControl = useCallback((requestedTab = null) => {
		setRouteControlRequestedTab(requestedTab);
		setShowRouteControl(true);
	}, []);

	const closeRouteControl = useCallback(() => {
		setShowRouteControl(false);
		setRouteControlRequestedTab(null);
	}, []);

	const openAssistantPharmacistSwitch = useCallback(() => {
		if (!canSwitchAssistantPharmacist || !pharmacyId) {
			return;
		}

		setAssistantSwitchError('');
		dispatch(fetchAllPharmacistList({ pharmacyId }));
		setShowAssistantPharmacistSwitch(true);
	}, [canSwitchAssistantPharmacist, dispatch, pharmacyId]);

	const handleAssistantPharmacistSwitch = useCallback(async (pharmacistId) => {
		if (!canSwitchAssistantPharmacist || !pharmacyId || !pharmacistId) {
			return;
		}

		setAssistantSwitchSaving(true);
		setAssistantSwitchError('');

		try {
			await dispatch(savePharmacistForDriver({
				reqData: {
					pharmacy_id: pharmacyId,
					pharmacist_id: pharmacistId,
				},
			})).unwrap();

			const pharmacistIdToStore = typeof pharmacistId === 'string'
				? parseInt(pharmacistId, 10)
				: pharmacistId;

			dispatch(authSelectedPharmacist(pharmacistIdToStore));
			dispatch(fetchPharmacistOfPharmacy(pharmacyId));
			setShowAssistantPharmacistSwitch(false);
		} catch (_) {
			setAssistantSwitchError('Unable to switch pharmacist. Please try again.');
		} finally {
			setAssistantSwitchSaving(false);
		}
	}, [canSwitchAssistantPharmacist, dispatch, pharmacyId]);

	useEffect(() => {
		if (!shouldAutoOpenRoutePicker || hasHandledAutoOpenRoutePickerRef.current) {
			return;
		}

		hasHandledAutoOpenRoutePickerRef.current = true;
		openTodayDispenseModal();
		navigate(routeNames.DASHBOARD_PHARMACY);
	}, [navigate, openTodayDispenseModal, shouldAutoOpenRoutePicker]);

	useEffect(() => {
		if (!shouldAutoOpenRouteControl) {
			hasHandledAutoOpenRouteControlRef.current = false;
			return;
		}

		if (hasHandledAutoOpenRouteControlRef.current) {
			return;
		}

		hasHandledAutoOpenRouteControlRef.current = true;
		const targetTab = routeControlTabParam === 'requests' ? TAB.REQUESTS : TAB.HANDOVER;
		openRouteControl(targetTab);
		navigate(routeNames.DASHBOARD_PHARMACY);
	}, [navigate, openRouteControl, routeControlTabParam, shouldAutoOpenRouteControl]);

	useEffect(() => {
		if (pharmacy_assign_chats && pharmacyId) {
			dispatch(getActivityStatus({ status: 'online', pharmacy_id: pharmacyId }));
		}
	}, [pharmacy_assign_chats, pharmacyId, dispatch]);

	const gridClass = 'dashboard-cards-grid grid grid-cols-2 gap-3';
	const yellowCard = 'bg-yellow-400 bg-opacity-10';
	const tealCard = 'bg-teal-400 bg-opacity-10';
	return (
		<>
			<div className="app-wrapper flex justify-center">
				<div className="app-screen no-shades w-full xl:w-3/12 lg:w-4/12 md:w-5/12 sm:w-6/12 overflow-x-hidden">
					<div className="relative p-4">
						{isLoading && (
							<div className="progress-loader">
								<div className="progress" />
							</div>
						)}

						<Common.TopBar />

						{showSearchBox ? (
							<div className="mb-4">
								<SearchBox
									pharmacyId={pharmacyId}
									inputClassName="!h-[58px] !rounded-[12px] !border !border-slate-300 !bg-slate-50 !pl-11 !pr-4 !text-[16px] !font-medium !text-slate-800 placeholder:!text-slate-400 focus:!ring-0 focus:!ring-transparent focus:!shadow-none focus:!outline-none"
									dropdownClassName="!mt-2 !rounded-[18px] !border-slate-200 !shadow-lg"
								/>
							</div>
						) : null}

						{flexiUser && hcpUser === 0 && (
							<div className={`${gridClass} my-2`}>
								<DashboardCard icon={<Icons.PatientConsent />} label="Add Vitals" cardClassName={tealCard} iconBoxStyle={{ ...ICON_BOX_STYLE, backgroundColor: 'rgb(15 118 110)' }} onClick={() => navigate(routeNames.ADD_VITALS)} />
								<DashboardCard icon={<Icons.PendingDispenseIcon />} label="Flexi Deliver" cardClassName={yellowCard} iconBoxStyle={{ ...ICON_BOX_STYLE, backgroundColor: 'rgb(161 98 7)' }} onClick={() => navigate(routeNames.flexi_delivery)} />
							</div>
						)}

						{!flexiUser && hcpUser === 0 && (
							<div className={`${gridClass} my-2`}>
								<DashboardCard icon={<Icons.PatientConsent />} label="Add Vitals" cardClassName={tealCard} iconBoxStyle={{ ...ICON_BOX_STYLE, backgroundColor: 'rgb(15 118 110)' }} onClick={() => navigate(routeNames.ADD_VITALS)} />
							</div>
						)}

						{flexiUser && hcpUser !== 0 && (
							<div className={`${gridClass} my-2`}>
								<DashboardCard icon={<Icons.PendingDispenseIcon />} label="Flexi Deliver" cardClassName={yellowCard} iconBoxStyle={{ ...ICON_BOX_STYLE, backgroundColor: 'rgb(161 98 7)' }} onClick={() => navigate(routeNames.flexi_delivery)} />
							</div>
						)}

						{!flexiUser && hcpUser !== 0 && (
							<div className={`${gridClass}`}>
								<DashboardCard icon={<Icons.TodayDispenseIcon />} label="Pick Route" cardClassName="btnnn bg-green-50" iconBoxStyle={{ ...ICON_BOX_STYLE, backgroundColor: 'rgb(21 128 61)' }} onClick={isLoading ? () => {} : openTodayDispenseModal} />
								<DashboardCard icon={<Icons.SwitchPharmacyIcon />} label="Route Control" cardClassName="bg-slate-100" iconBoxStyle={{ ...ICON_BOX_STYLE, backgroundColor: 'rgb(51 65 85)' }} onClick={() => openRouteControl()} badge={pendingHandoverCount} />
								{showSupervisorCard && (
									<DashboardCard icon={<Icons.AnalyticsIcon />} label="Supervisor" cardClassName="bg-gray-400 bg-opacity-10" iconBoxStyle={{ ...ICON_BOX_STYLE, backgroundColor: 'rgb(55 65 81)' }} onClick={() => navigate(routeNames.today_analytics)} />
								)}

								<DashboardCard icon={<Icons.PendingDispenseIcon />} label="Pending Dispense" cardClassName="bg-purple-400 bg-opacity-10" iconBoxStyle={{ ...ICON_BOX_STYLE, backgroundColor: 'rgb(126 34 206)' }} onClick={isLoading ? () => {} : () => navigate(routeNames.driver_today_pending)} />

								<DashboardCard icon={<Icons.PendingDispenseIcon />} label="Flexi Deliver" cardClassName={yellowCard} iconBoxStyle={{ ...ICON_BOX_STYLE, backgroundColor: 'rgb(161 98 7)' }} onClick={() => navigate(routeNames.flexi_delivery)} />
								<DashboardCard icon={<Icons.CrossIcon />} label="Missed Medication" cardClassName="bg-red-400 bg-opacity-10" iconBoxStyle={{ ...ICON_BOX_STYLE, backgroundColor: '#c12323' }} onClick={() => navigate(routeNames.PICKED_PATIENTS)} />

								{showMissedDispense && (
									<DashboardCard icon={<Icons.CrossIcon />} label="Missed Dispense" cardClassName="bg-pink-400 bg-opacity-10" iconBoxStyle={{ ...ICON_BOX_STYLE, backgroundColor: 'rgb(190 18 60)' }} onClick={() => navigate(routeNames.driver_Missed_Route)} />
								)}

								<DashboardCard icon={<Icons.ClinicAppointmentIcon />} label="Clinic Appointments" cardClassName="bg-emerald-400 bg-opacity-10" iconBoxStyle={{ ...ICON_BOX_STYLE, backgroundColor: 'rgb(5 150 105)' }} onClick={() => navigate(routeNames.TODAY_APPOINTMENT_DELIVERIES)} />

								{showAddVitalsCard && (
									<DashboardCard icon={<Icons.PatientConsent />} label="Add Vitals" cardClassName={tealCard} iconBoxStyle={{ ...ICON_BOX_STYLE, backgroundColor: 'rgb(15 118 110)' }} onClick={() => navigate(routeNames.ADD_VITALS)} />
								)}

								<DashboardCard icon={<Icons.UploadPrescritption />} label="Upload Prescription" cardClassName="bg-cyan-400 bg-opacity-10" iconBoxStyle={{ ...ICON_BOX_STYLE, background: '#4d7c0f' }} cardStyle={{ background: 'rgb(237 241 227 / 72%)' }} onClick={() => navigate(routeNames.upload_prescripition)} />

								<DashboardCard icon={<Icons.PatientConsent />} label="Patient Consent" cardClassName="bg-orange-300 bg-opacity-10" iconBoxStyle={{ ...ICON_BOX_STYLE, backgroundColor: 'orange' }} onClick={() => navigate(routeNames.Patient_Consent)} />

								{showSwitchPharmacy && (
									<DashboardCard icon={<Icons.SwitchPharmacyIcon />} label="Switch Pharmacy" cardClassName="btnnn bg-red-400 bg-opacity-10" iconBoxStyle={{ ...ICON_BOX_STYLE, backgroundColor: 'rgb(185 28 28)' }} onClick={() => navigate(routeNames.SWITCH_PHARMACY)} />
								)}

								{canSwitchAssistantPharmacist && (
									<DashboardCard
										icon={<Icons.SwitchPharmacyIcon />}
										label="Switch Pharmacist"
										cardClassName="btnnn bg-fuchsia-400 bg-opacity-10"
										iconBoxStyle={{ ...ICON_BOX_STYLE, backgroundColor: 'rgb(192 38 211)' }}
										onClick={openAssistantPharmacistSwitch}
									/>
								)}

								<DashboardCard icon={<Icons.ContactIcon />} label="Contact Pharmacy" cardClassName="bg-blue-50" iconBoxStyle={{ ...ICON_BOX_STYLE, backgroundColor: 'rgb(29 78 216)' }} onClick={() => navigate(routeNames.PHARMACY_FRONT_PROFILE)} />

								<DashboardCard icon={<Icons.ToastMessageIcon />} label="Updates" cardClassName="bg-indigo-400 bg-opacity-10" iconBoxStyle={{ ...ICON_BOX_STYLE, backgroundColor: 'rgb(67 56 202)' }} onClick={() => navigate(routeNames.updates)} />

								<DashboardCard icon={<Icons.ProfileSetting />} label="Profile" cardClassName="bg-opacity-10" iconBoxStyle={{ ...ICON_BOX_STYLE, backgroundColor: 'rgb(104 30 71)' }} cardStyle={{ backgroundColor: 'rgb(104 30 71 / var(--tw-bg-opacity))' }} onClick={() => navigate(routeNames.MY_PROFILE)} />
							</div>
						)}

						{!pharmacy_assign_chats && <Common.BottomBar pharmacyId={pharmacyId} />}

						<RoutePharmacistModal showModel={showModel} setShowModel={setShowModel} isDelivered={isDelivered} />
						<RouteControlSheet
							isOpen={showRouteControl}
							onClose={closeRouteControl}
							onPendingRequestsChange={setPendingHandoverCount}
							onPickRoute={openTodayDispenseModal}
							requestedTab={routeControlRequestedTab}
						/>
						<AssistantPharmacistSwitchModal
							isOpen={showAssistantPharmacistSwitch}
							onClose={() => setShowAssistantPharmacistSwitch(false)}
							allPharmacistListResponse={allPharmacistListResponse}
							currentPharmacistId={currentAssistantPharmacistId}
							onSave={handleAssistantPharmacistSwitch}
							saving={assistantSwitchSaving}
							errorMessage={assistantSwitchError}
						/>
					</div>
				</div>
			</div>
		</>
	);
};

export default DriverDashboard;








































// import React, {
// 	useEffect,
// 	useState,
// } from 'react';

// import { RoutePharmacistModal } from '../../components/common/routePharmacistModal';
// import { useAllSelector } from '../../services/selectors/allSelector';
// import { useAuthSelector } from '../../services/selectors/authSelector';

// import { fetchPharmacistOfPharmacy } from '../../services/apis/dispense/pharmacistOfPharmacy';
// import { fetchCheckUnassignedVitalPatients } from '../../services/slices/supervisor/pendingCounselling';
// import { useLocation } from 'react-router-dom';

// import { clearAllTodayDispenseData } from '../../services/slices/dispense/patientForRouteList';
// import { SOSemergencyEnableButton } from '../../components/common/SOSemergencyEnableButton';
// import { getActivityStatus } from '../../services/slices/chat/activityStatus';

// import SearchBox from '../../components/common/searchBox';

// import * as Icons from '../../assets/svg/icons';
// import * as Common from '../../components/common';
// import * as Hooks from '../../hooks';
// import * as routeNames from '../../routes/routeName';

// const DriverDashboard = () => {

// 	const navigate = Hooks.useCustomNavigate();
// 	const dispatch = Hooks.useCustomDispatch();
// 	const location = useLocation()

// 	const [isDelivered, setIsDelivered] = useState(false);
// 	const [showModel, setShowModel] = useState(false);
// 	const [apiCallMade, setApiCallMade] = useState(false)

// 	const { finalMissedCount,
// 		pharmacistOfPharmacyListResponse,
// 		supervisor_pharmacist,
// 		supervisorOfTheDay,
// 		pharmacy_assign_chats,
// 		hasUnassignedVital
// 	} = useAllSelector()

// 	const { pharmacyId, isSupervisor, authPharmacy, flexiUser, hcpUser } = useAuthSelector()

// 	useEffect(() => {
// 		window.scrollTo(0, 0);
// 	}, []);

// 	useEffect(() => {
// 		if (pharmacyId && !apiCallMade && (location?.pathname === routeNames.DASHBOARD_PHARMACY)) {
// 			dispatch(fetchPharmacistOfPharmacy(pharmacyId))
// 			dispatch(fetchCheckUnassignedVitalPatients({ pharmacyId }))
// 			dispatch(clearAllTodayDispenseData())
// 			setApiCallMade(true)
// 		}
// 		return () => { }
// 	}, [apiCallMade, pharmacyId, location.pathname, dispatch])

// 	const openTodayDispenseModal = () => {
// 		setIsDelivered(false)
// 		setShowModel(true)
// 	}

// 	useEffect(() => {
// 		if (pharmacy_assign_chats && pharmacyId) dispatch(getActivityStatus({ status: 'online', pharmacy_id: pharmacyId }));
// 	}, [pharmacy_assign_chats, pharmacyId, dispatch])

// 	return (
// 		<>
// 			<div className="app-wrapper flex justify-center ">
// 				<div className="app-screen no-shades w-full xl:w-3/12 lg:w-4/12 md:w-5/12 sm:w-6/12  overflow-x-hidden">
// 					<div className='flex items-center justify-center'>
// 						<SOSemergencyEnableButton />
// 					</div>

// 					<div className="relative p-4">
// 						{pharmacistOfPharmacyListResponse?.loading &&
// 							<div className="progress-loader">
// 								<div className="progress"></div>
// 							</div>
// 						}

// 						<div className="card bg-white p-4 rounded-lg shadow-sm">
// 							<div className='flex'><div>
// 								<Common.TopBar pharmacyId={pharmacyId} />
// 							</div>
// 							</div>
// 						</div>

// 						{hcpUser === 0 && flexiUser ? null : hcpUser === 0 ? null : flexiUser ? null : <>
// 							<div className='flex gap-3 mb-3'>
// 								<SearchBox pharmacyId={pharmacyId} />
// 							</div>
// 						</>
// 						}

// 						{

// 							flexiUser && hcpUser === 0 ?
// 								<div className="dashboard-cards-grid grid grid-cols-2 gap-3 my-2">
// 									<div className="card flex flex-col p-3 rounded-md bg-yellow-400 bg-opacity-10 gap-3" onClick={() => navigate(`${routeNames.ADD_VITALS}`)}>
// 										<div className="rounded-md bg-yellow-700 flex items-center justify-center" style={{ width: '40px', height: '40px' }}>
// 											<Icons.PatientConsent />
// 										</div>
// 										<div className="text-info">
// 											<h1 className="text-sm font-medium"
// 												style={{ fontSize: '13px', fontWeight: '500' }}>Add Vitals</h1>
// 										</div>
// 									</div>


// 									<div className="card flex flex-col p-3 rounded-md bg-yellow-400 bg-opacity-10 gap-3" onClick={() => navigate(`${routeNames.flexi_delivery}`)}>
// 										<div className="rounded-md bg-yellow-700 flex items-center justify-center" style={{ width: '40px', height: '40px' }}>
// 											<Icons.PendingDispenseIcon />
// 										</div>
// 										<div className="text-info">
// 											<h1 className="text-sm font-medium" style={{ fontSize: '13px', fontWeight: '500' }}>Flexi Deliver</h1>
// 										</div>
// 									</div>

// 								</div>
// 								: hcpUser === 0 ?
// 									<div className="dashboard-cards-grid grid grid-cols-2 gap-3 my-2">
// 										<div className="card flex flex-col p-3 rounded-md bg-yellow-400 bg-opacity-10 gap-3" onClick={() => navigate(`${routeNames.ADD_VITALS}`)}>
// 											<div className="rounded-md bg-yellow-700 flex items-center justify-center" style={{ width: '40px', height: '40px' }}>
// 												<Icons.PatientConsent />
// 											</div>
// 											<div className="text-info">
// 												<h1 className="text-sm font-medium"
// 													style={{ fontSize: '13px', fontWeight: '500' }}>Add Vitals</h1>
// 											</div>
// 										</div>
// 									</div>
// 									:
// 									flexiUser ?
// 										<div className="dashboard-cards-grid grid grid-cols-2 gap-3 my-2">
// 											<div className="card flex flex-col p-3 rounded-md bg-yellow-400 bg-opacity-10 gap-3" onClick={() => navigate(`${routeNames.flexi_delivery}`)}>
// 												<div className="rounded-md bg-yellow-700 flex items-center justify-center" style={{ width: '40px', height: '40px' }}>
// 													<Icons.PendingDispenseIcon />
// 												</div>
// 												<div className="text-info">
// 													<h1 className="text-sm font-medium" style={{ fontSize: '13px', fontWeight: '500' }}>Flexi Deliver</h1>
// 												</div>
// 											</div>
// 										</div>
// 										:
// 										<div className="dashboard-cards-grid grid grid-cols-2 gap-3">
// 											<div className="card flex flex-col p-3 rounded-md btnnn bg-green-50 gap-3"
// 												onClick={() => pharmacistOfPharmacyListResponse?.loading ? null : openTodayDispenseModal()}>
// 												<div className="rounded-md bg-green-700 flex items-center justify-center"
// 													style={{ width: '40px', height: '40px' }}>
// 													<Icons.TodayDispenseIcon />
// 												</div>
// 												<div className="text-info">
// 													<h1 className="text-sm font-medium" style={{ fontSize: '13px', fontWeight: '500' }}>Pick Route</h1>
// 												</div>
// 											</div>


// 											{((isSupervisor === 1 && supervisor_pharmacist) || (supervisorOfTheDay)) &&
// 												<div className="card flex flex-col p-3 rounded-md bg-gray-400 bg-opacity-10 gap-3"
// 													onClick={() => navigate(`${routeNames.today_analytics}`)}>
// 													<div className="rounded-md bg-gray-700 flex items-center justify-center"
// 														style={{ width: '40px', height: '40px' }}>
// 														<Icons.AnalyticsIcon />
// 													</div>
// 													<div className="text-info">
// 														<h1 className="text-sm font-medium" style={{ fontSize: '13px', fontWeight: '500' }}>Supervisor</h1>
// 													</div>
// 												</div>}

// 											<div className="card flex flex-col p-3 rounded-md bg-cyan-400 bg-opacity-10 gap-3"
// 												onClick={() => navigate(`${routeNames.upload_prescripition}`)} style={{ background: 'rgb(237 241 227 / 72%)' }}>
// 												<div className="rounded-md bg-cyan-700 flex items-center justify-center"
// 													style={{ width: '40px', height: '40px', background: '#4d7c0f' }}>
// 													<Icons.UploadPrescritption />
// 												</div>
// 												<div className="text-info">
// 													<h1 className="text-sm font-medium" style={{ fontSize: '13px', fontWeight: '500' }}>Upload Prescription</h1>
// 												</div>
// 											</div>

// 											{pharmacistOfPharmacyListResponse?.data === null ? null :
// 												finalMissedCount !== 0 &&
// 												<div className="card flex flex-col p-3 rounded-md bg-pink-400 bg-opacity-10 gap-3"
// 													onClick={() => navigate(`${routeNames.driver_Missed_Route}`)}>
// 													<div className="rounded-md bg-pink-700 flex items-center justify-center"
// 														style={{ width: '40px', height: '40px' }}>
// 														<Icons.CrossIcon />
// 													</div>
// 													<div className="text-info">
// 														<h1 className="text-sm font-medium" style={{ fontSize: '13px', fontWeight: '500' }}>Missed Dispense</h1>
// 													</div>
// 												</div>}

// 											<div className="card flex flex-col p-3 rounded-md bg-purple-400 bg-opacity-10 gap-3"
// 												onClick={() => pharmacistOfPharmacyListResponse?.loading ? null : navigate(`${routeNames.driver_today_pending}`)} >
// 												<div className="rounded-md bg-purple-700 flex items-center justify-center"
// 													style={{ width: '40px', height: '40px' }}>
// 													<Icons.PendingDispenseIcon />
// 												</div>
// 												<div className="text-info">
// 													<h1 className="text-sm font-medium" style={{ fontSize: '13px', fontWeight: '500' }}>Pending Dispense</h1>
// 												</div>
// 											</div>

// 											<div className="card flex flex-col p-3 rounded-md bg-yellow-400 bg-opacity-10 gap-3" onClick={() => navigate(`${routeNames.flexi_delivery}`)}>
// 												<div className="rounded-md bg-yellow-700 flex items-center justify-center" style={{ width: '40px', height: '40px' }}>
// 													<Icons.PendingDispenseIcon />
// 												</div>
// 												<div className="text-info">
// 													<h1 className="text-sm font-medium" style={{ fontSize: '13px', fontWeight: '500' }}>Flexi Deliver</h1>
// 												</div>
// 											</div>

// 											{
// 												(hcpUser === 1 || hasUnassignedVital) &&
// 												<div className="card flex flex-col p-3 rounded-md bg-yellow-400 bg-opacity-10 gap-3" onClick={() => navigate(`${routeNames.ADD_VITALS}`)}>
// 													<div className="rounded-md bg-yellow-700 flex items-center justify-center" style={{ width: '40px', height: '40px' }}>
// 														<Icons.PatientConsent />
// 													</div>
// 													<div className="text-info">
// 														<h1 className="text-sm font-medium"
// 															style={{ fontSize: '13px', fontWeight: '500' }}>Add Vitals</h1>
// 													</div>
// 												</div>
// 											}

// 											<div className="card flex flex-col p-3 rounded-md bg-indigo-400 bg-opacity-10 gap-3" onClick={() => navigate(`${routeNames.updates}`)}>
// 												<div className="rounded-md bg-indigo-700 flex items-center justify-center" style={{ width: '40px', height: '40px' }}>
// 													<Icons.ToastMessageIcon />
// 												</div>
// 												<div className="text-info">
// 													<h1 className="text-sm font-medium" style={{ fontSize: '13px', fontWeight: '500' }}>Updates</h1>
// 												</div>
// 											</div>

// 											{authPharmacy?.length === 1 ? null :
// 												<div className="card btnnn flex flex-col p-3 rounded-md bg-red-400 bg-opacity-10 gap-3"
// 													onClick={() => navigate(routeNames.SWITCH_PHARMACY)} >
// 													<div className="rounded-md bg-red-700 flex items-center justify-center"
// 														style={{ width: '40px', height: '40px' }}>
// 														<Icons.SwitchPharmacyIcon />
// 													</div>
// 													<div className="text-info">
// 														<h1 className="text-sm font-medium" style={{ fontSize: '13px', fontWeight: '500' }}>Switch Pharmacy</h1>
// 													</div>
// 												</div>}

// 											<div className="card flex flex-col p-3 rounded-md bg-orange-300 bg-opacity-10 gap-3" onClick={() => navigate(`${routeNames.Patient_Consent}`)} >
// 												<div className="rounded-md flex items-center justify-center"
// 													style={{ width: '40px', height: '40px', backgroundColor: 'orange' }}>
// 													<Icons.PatientConsent />
// 												</div>
// 												<div className="text-info">
// 													<h1 className="text-sm font-medium" style={{ fontSize: '13px', fontWeight: '500' }}>Patient Consent</h1>
// 												</div>
// 											</div>

// 											<div className="card flex flex-col p-3 rounded-md bg-blue-50 gap-3" onClick={() => navigate(`${routeNames.PHARMACY_FRONT_PROFILE}`)}>
// 												<div className="rounded-md bg-blue-700 flex items-center justify-center"
// 													style={{ width: '40px', height: '40px' }}>
// 													<Icons.ContactIcon />
// 												</div>
// 												<div className="text-info">
// 													<h1 className="text-sm font-medium" style={{ fontSize: '13px', fontWeight: '500' }}>Contact Pharmacy</h1>
// 												</div>
// 											</div>

// 											<div className="card flex flex-col p-3 rounded-md bg-opacity-10 gap-3" onClick={() => navigate(`${routeNames.MY_PROFILE}`)} style={{ backgroundColor: 'rgb(104 30 71 / var(--tw-bg-opacity))' }}>
// 												<div className="rounded-md flex items-center justify-center"
// 													style={{ width: '40px', height: '40px', backgroundColor: 'rgb(104 30 71 )' }}>
// 													<Icons.ProfileSetting />
// 												</div>
// 												<div className="text-info">
// 													<h1 className="text-sm font-medium" style={{ fontSize: '13px', fontWeight: '500' }}>Profile</h1>
// 												</div>
// 											</div>
// 										</div>
// 						}

// 						{pharmacy_assign_chats ? null :
// 							<Common.BottomBar pharmacyId={pharmacyId} />}


// 						<RoutePharmacistModal
// 							showModel={showModel}
// 							setShowModel={setShowModel}
// 							isDelivered={isDelivered} />

// 					</div>
// 				</div>
// 			</div>

// 			{/* <ChatBox /> */}
// 		</>
// 	)
// }

// export default DriverDashboard
